import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MdCheck, MdChevronLeft, MdChevronRight, MdPriorityHigh, MdRemove } from "react-icons/md";
import { profilePath } from "components/assessment/audiences";
import Badge from "components/badge";
import Button from "components/button";
import Card, { CardHeader } from "components/card";
import Dialog from "components/dialog";
import Tooltip from "components/tooltip";
import { getHealthLevelTone, getRatingLabel, getRatingTextClass, hasBlocker } from "utils/healthScoring";
import { EMPTY, formatDate } from "utils/format";

const TONE_FILL = {
  success: "bg-success-subtle text-success-fg ring-1 ring-inset ring-success/30",
  warning: "bg-warning-subtle text-warning-fg ring-1 ring-inset ring-warning/30",
  danger: "bg-danger-subtle text-danger-fg ring-1 ring-inset ring-danger/30",
};

const healthStatus = (level, label, icon) => {
  const tone = getHealthLevelTone(level);
  return { label, tone, fill: TONE_FILL[tone], icon };
};

const HEALTH_STATUS = {
  healthy: healthStatus("healthy", "Healthy", MdCheck),
  monitor: healthStatus("monitor", "Monitor", MdRemove),
  elevated: healthStatus("elevated", "Elevated", MdPriorityHigh),
};

const getHealthStatus = (satisfaction, contribution) => {
  const avg = ((satisfaction || 0) + (contribution || 0)) / 2;
  if (avg >= 4) return "healthy";
  if (avg >= 3) return "monitor";
  return "elevated";
};

/** Short client-side analysis blurb from scores and answers. */
const buildQuickAnalysis = (response, status) => {
  const sat = response.roleSatisfaction || 0;
  const contrib = response.contributionScore || 0;
  const name = response.staffName?.split(" ")[0] || "This person";

  const parts = [];

  if (status === "elevated") {
    if (sat <= 2 && contrib <= 2) {
      parts.push(`${name} is reporting low satisfaction and low contribution this week, which may signal disengagement or feeling stuck.`);
    } else if (sat <= 2) {
      parts.push(`${name}'s satisfaction is notably low this week despite reasonable contribution. It may be worth checking whether the role still feels like a good fit.`);
    } else if (contrib <= 2) {
      parts.push(`${name} feels their contributions aren't landing this week, even though satisfaction is higher. They may need clearer direction or resources.`);
    } else {
      parts.push(`${name}'s scores suggest a tough week overall.`);
    }
  } else if (status === "monitor") {
    if (sat === 3 && contrib === 3) {
      parts.push(`${name} is in a neutral zone this week: not struggling, but not thriving either. A brief check-in could surface what's keeping them from feeling fully engaged.`);
    } else if (sat === 3) {
      parts.push(`${name}'s satisfaction is middling this week. It may be worth a casual conversation to see what would move the needle.`);
    } else {
      parts.push(`${name}'s contribution score is moderate. They may benefit from clearer priorities or encouragement.`);
    }
  } else if (sat >= 5 && contrib >= 5) {
    parts.push(`${name} is firing on all cylinders, with high satisfaction and high contribution. This is a great week to recognize their impact.`);
  } else {
    parts.push(`${name} is in a healthy place this week with solid scores across the board.`);
  }

  if (hasBlocker(response)) {
    parts.push("They flagged a blocker that may need leadership support to resolve.");
  }

  return parts.join(" ");
};

function RatingTile({ label, rating }) {
  return (
    <div className="rounded-control bg-surface-subtle p-3">
      <p className="text-xs text-fg-muted">{label}</p>
      {rating > 0 ? (
        <p className={`text-lg font-bold ${getRatingTextClass(rating)}`}>
          {rating} <span className="text-xs font-normal">/ 5 · {getRatingLabel(rating)}</span>
        </p>
      ) : (
        <p className="text-sm text-fg-muted">{EMPTY}</p>
      )}
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div>
      <h3 className="mb-0.5 text-xs font-semibold uppercase tracking-wider text-fg-muted">{title}</h3>
      {children}
    </div>
  );
}

function ResponseDetails({ response }) {
  const statusKey = getHealthStatus(response.roleSatisfaction, response.contributionScore);
  const status = HEALTH_STATUS[statusKey];
  const hasScores = response.roleSatisfaction > 0 || response.contributionScore > 0;

  return (
    <div className="space-y-4">
      <Badge tone={status.tone}>{status.label}</Badge>

      <div className="grid grid-cols-2 gap-3">
        <RatingTile label="Satisfaction" rating={response.roleSatisfaction} />
        <RatingTile label="Contribution" rating={response.contributionScore} />
      </div>

      {hasScores && (
        <div className="rounded-control bg-info-subtle p-3">
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-info-fg">
            Quick Analysis
          </h3>
          <p className="text-sm leading-relaxed text-fg">
            {response.aiSummary || buildQuickAnalysis(response, statusKey)}
          </p>
        </div>
      )}

      {response.mainWork && (
        <DetailSection title="What They Worked On">
          <p className="text-sm text-fg">{response.mainWork}</p>
        </DetailSection>
      )}
      {response.story && (
        <DetailSection title="Story / Highlight">
          <p className="text-sm text-fg">{response.story}</p>
        </DetailSection>
      )}
      {hasBlocker(response) && (
        <DetailSection title="Blockers">
          <p className="text-sm text-warning-fg">{response.blockers}</p>
        </DetailSection>
      )}
    </div>
  );
}

function HeatCell({ name, week, response, onSelect }) {
  if (!response) {
    return (
      <div
        role="img"
        aria-label={`${name}, week of ${week.label}: no response`}
        className="relative mx-auto h-7 w-full max-w-16 overflow-hidden rounded-control bg-surface-subtle"
      >
        <svg
          aria-hidden="true"
          className="absolute inset-0 h-full w-full text-fg-muted/60"
          preserveAspectRatio="none"
          viewBox="0 0 100 100"
        >
          <line x1="15" y1="85" x2="85" y2="15" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      </div>
    );
  }

  const status = HEALTH_STATUS[getHealthStatus(response.roleSatisfaction, response.contributionScore)];
  const StatusIcon = status.icon;
  const satisfaction = response.roleSatisfaction || EMPTY;
  const contribution = response.contributionScore || EMPTY;

  return (
    <Tooltip
      content={
        <>
          <span className="block">
            {name} · {week.label} · {status.label}
          </span>
          <span className="block font-normal">
            Satisfaction: {satisfaction} · Contribution: {contribution}
          </span>
        </>
      }
      trigger={
        <button
          type="button"
          aria-label={`${name}, week of ${week.label}: ${status.label}. Satisfaction ${satisfaction}, contribution ${contribution}`}
          onClick={() => onSelect({ response, weekLabel: week.label })}
          className={`focus-ring mx-auto flex h-7 w-full max-w-16 items-center justify-center rounded-control transition-opacity hover:opacity-80 ${status.fill}`}
        >
          <StatusIcon aria-hidden="true" className="h-4 w-4" />
        </button>
      }
    />
  );
}

function Legend() {
  return (
    <ul className="mt-3 flex flex-wrap gap-4 text-xs text-fg-muted">
      {Object.values(HEALTH_STATUS).map(({ label, fill, icon: Icon }) => (
        <li key={label} className="flex items-center gap-1.5">
          <span className={`inline-flex h-4 w-4 items-center justify-center rounded ${fill}`}>
            <Icon aria-hidden="true" className="h-3 w-3" />
          </span>
          {label}
        </li>
      ))}
      <li className="flex items-center gap-1.5">
        <span className="relative inline-block h-4 w-4 overflow-hidden rounded bg-surface-subtle">
          <svg
            aria-hidden="true"
            className="absolute inset-0 h-full w-full text-fg-muted/60"
            preserveAspectRatio="none"
            viewBox="0 0 100 100"
          >
            <line x1="15" y1="85" x2="85" y2="15" stroke="currentColor" strokeWidth="8" strokeLinecap="round" />
          </svg>
        </span>
        No Response
      </li>
    </ul>
  );
}

const TeamHealthHeatmap = ({ config, summaries, weeksLoaded, onLoadMore, onLoadLess }) => {
  const [selected, setSelected] = useState(null);

  const { names, weeks, userIdMap } = useMemo(() => {
    const ordered = [...(summaries || [])].reverse();

    const nameSet = new Set();
    const uidMap = {};
    ordered.forEach((week) => {
      (week.responses || []).forEach((r) => {
        if (r.staffName) {
          nameSet.add(r.staffName);
          if (r.userId && !uidMap[r.staffName]) uidMap[r.staffName] = r.userId;
        }
      });
    });

    const weekColumns = ordered.map((week) => {
      const responseMap = {};
      (week.responses || []).forEach((r) => {
        if (r.staffName) responseMap[r.staffName] = r;
      });
      return { label: formatDate(week.weekOf), weekOf: week.weekOf, responseMap };
    });

    return { names: [...nameSet].sort(), weeks: weekColumns, userIdMap: uidMap };
  }, [summaries]);

  if (!summaries || summaries.length === 0 || names.length === 0) {
    return null;
  }

  const canLoadLess = weeksLoaded > 8;
  const hasMoreData = summaries.length >= weeksLoaded;
  const selectedResponse = selected?.response;

  return (
    <Card padding="md">
      <CardHeader
        title="Team Health Heatmap"
        description={`Showing ${weeks.length} weeks. Select a cell for details.`}
        actions={
          <>
            {canLoadLess && (
              <Button variant="secondary" size="sm" leftIcon={<MdChevronLeft />} onClick={onLoadLess}>
                Fewer
              </Button>
            )}
            {hasMoreData && (
              <Button variant="secondary" size="sm" rightIcon={<MdChevronRight />} onClick={onLoadMore}>
                More
              </Button>
            )}
          </>
        }
      />

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th scope="col" className="w-36 pb-2 pr-4 text-left text-xs font-medium text-fg-muted">
                {config.labels.role}
              </th>
              {weeks.map((w) => (
                <th
                  key={w.weekOf}
                  scope="col"
                  className="min-w-20 whitespace-nowrap px-1 pb-2 text-center text-xs font-medium text-fg-muted"
                >
                  {w.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {names.map((name) => (
              <tr key={name}>
                <th scope="row" className="whitespace-nowrap py-1 pr-4 text-left text-xs font-medium">
                  {userIdMap[name] ? (
                    <Link
                      to={profilePath(config, userIdMap[name])}
                      className="focus-ring rounded text-primary hover:underline dark:text-accent"
                    >
                      {name}
                    </Link>
                  ) : (
                    <span className="text-fg">{name}</span>
                  )}
                </th>
                {weeks.map((week) => (
                  <td key={week.weekOf} className="p-1">
                    <HeatCell
                      name={name}
                      week={week}
                      response={week.responseMap[name]}
                      onSelect={setSelected}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Legend />

      <Dialog
        isOpen={Boolean(selectedResponse)}
        onClose={() => setSelected(null)}
        title={selectedResponse?.staffName}
        description={
          selectedResponse && (
            <>
              {selectedResponse.staffRole && (
                <span className="capitalize">{selectedResponse.staffRole} · </span>
              )}
              Week of {selected.weekLabel}
              {selectedResponse.submittedAt && <> · Submitted {formatDate(selectedResponse.submittedAt)}</>}
            </>
          )
        }
      >
        {selectedResponse && <ResponseDetails response={selectedResponse} />}
      </Dialog>
    </Card>
  );
};

export default TeamHealthHeatmap;
